import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetMembersQuery,
  useCreateGroupMutation,
  useAddGroupMemberMutation,
} from "../../infrastructure/api/membersGroupsApi";

interface Props {
  open: boolean;
  onOpenChange: (val: boolean) => void;
}

export const CreateGroupDialog = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation("membersGroups");
  const [groupName, setGroupName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<
    { id: string; name: string }[]
  >([]);

  const { data: members = [], isLoading: membersLoading } = useGetMembersQuery(
    undefined,
    { skip: !open },
  );

  const [createGroup, { isLoading: creating }] = useCreateGroupMutation();
  const [addGroupMember] = useAddGroupMemberMutation();

  const addMember = (memberId: string) => {
    const member = members.find((m) => m.id === memberId);
    if (member && !selectedMembers.some((m) => m.id === memberId)) {
      setSelectedMembers([
        ...selectedMembers,
        { id: member.id, name: member.name },
      ]);
    }
  };

  const removeMember = (memberId: string) => {
    setSelectedMembers(selectedMembers.filter((m) => m.id !== memberId));
  };

  const handleClose = () => {
    setGroupName("");
    setSelectedMembers([]);
    onOpenChange(false);
  };

  const handleCreate = async () => {
    if (!groupName.trim()) return;
    try {
      const group = await createGroup({ name: groupName.trim() }).unwrap();
      await Promise.all(
        selectedMembers.map((m) =>
          addGroupMember({ groupId: group.id, userId: m.id }).unwrap(),
        ),
      );
      handleClose();
    } catch {
      // errors handled silently; toast can be added here
    }
  };

  const isValid = groupName.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-xl p-0 sm:max-w-[600px]">
        {/* Header */}
        <DialogHeader className="flex flex-row items-center justify-between px-6 py-4 border-b">
          <DialogTitle className="text-lg font-semibold">
            {t("createGroupDialog.title")}
          </DialogTitle>
        </DialogHeader>

        {/* Body */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6">
          {/* Group Name */}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t("createGroupDialog.nameLabel")} <span className="text-destructive-text">*</span>
            </label>
            <Input
              placeholder={t("createGroupDialog.namePlaceholder")}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          </div>

          {/* Add Members */}
          <div className="space-y-1">
            <label className="text-sm font-medium">{t("createGroupDialog.addMembersLabel")}</label>

            <Select
              value=""
              onValueChange={addMember}
              disabled={membersLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={membersLoading ? t("createGroupDialog.loading") : t("createGroupDialog.selectMember")}
                />
              </SelectTrigger>
              <SelectContent>
                {members
                  .filter((m) => !selectedMembers.some((s) => s.id === m.id))
                  .map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            {/* Selected Chips */}
            {selectedMembers.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-sm"
                  >
                    {member.name}
                    <button onClick={() => removeMember(member.id)}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="flex h-20 shrink-0 justify-end gap-3 border-t px-6 py-4">
          <Button
            variant="outline"
            className="border-primary text-primary cursor-pointer hover:bg-white hover:text-primary"
            onClick={handleClose}
            disabled={creating}
          >
            {t("createGroupDialog.cancel")}
          </Button>

          <Button
            className="bg-primary text-white hover:bg-primary cursor-pointer"
            onClick={handleCreate}
            disabled={!isValid}
            loading={creating}
          >
            {t("createGroupDialog.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
